<template>
  <q-dialog v-model="verifyCurrentPasswordDialog" transition-show="fade" transition-hide="fade">
    <div class="initialBackDrop" style="min-height: 100vh">
      <div class="q-pa-md q-gutter-sm" style="width: 102ex; margin-left: auto; margin-right: auto; margin-top: 5%; margin-bottom: auto; border-radius: 10px;">
        <q-card style="padding: 50px; border-radius: 10px;" class="q-pa-md">
          <div align="right"><q-btn icon="close" flat round dense v-close-popup class="circle-btn text-primary" style="margin: -70px -40px 0px 0px;" :disable="disabledFields" /></div>
          <div class="dialogText">Verify Current Password</div>

          <br />

          <q-form
            @submit="submitCurrentPassword"
            class="q-gutter-md"
          >
            <div style="display: flex; alignItems: baseline">
              <div>Current Password *</div>
              <q-space />
              <q-input
                v-model="password"
                filled
                style="width: 75%;"
                maxlength="256"
                :type="passwordVisibility ? 'password' : 'text'"
                :rules="[ val => val && val.length > 5 || 'Invalid minimum password length']"
                :disable="disabledFields"
              >
                <template v-slot:append>
                  <q-icon
                    :name="passwordVisibility ? 'visibility_off' : 'visibility'"
                    class="cursor-pointer"
                    @click="passwordVisibility = !passwordVisibility"
                  />
                </template>
              </q-input>
            </div>

            <br />

            <div style="display: flex; alignItems: baseline">
              <q-space />
              <q-btn
                label="Submit"
                type="submit"
                color="primary"
                no-caps
                :disable="disabledFields"
              />
              <q-btn
                label="Cancel"
                color="cancelButton"
                class="q-ml-sm"
                no-caps
                v-close-popup
                :disable="disabledFields"
              />
              <q-space />
            </div>
          </q-form>
        </q-card>
      </div>
    </div>
  </q-dialog>

  <q-dialog v-model="changePasswordDialog" transition-show="fade" transition-hide="fade">
    <div class="initialBackDrop" style="min-height: 100vh">
      <div class="q-pa-md q-gutter-sm" style="width: 102ex; margin-left: auto; margin-right: auto; margin-top: 5%; margin-bottom: auto; border-radius: 10px;">
        <q-card style="padding: 50px; border-radius: 10px;" class="q-pa-md">
          <div align="right"><q-btn icon="close" flat round dense v-close-popup class="circle-btn text-primary" style="margin: -70px -40px 0px 0px;" :disable="disabledFields" /></div>
          <div class="dialogText">Change Password</div>

          <br />

          <q-form
            @submit="submitChangePassword"
            class="q-gutter-md"
          >
            <div style="display: flex; alignItems: baseline">
              <div>Password *</div>
              <q-space />
              <q-input
                v-model="password"
                filled
                style="width: 75%;"
                maxlength="256"
                :type="passwordVisibility ? 'password' : 'text'"
                :rules="[
                  (val) =>
                    validatePassword(val) || 'Password must meet all criteria.'
                ]"
                :disable="disabledFields"
              >
                <template v-slot:append>
                  <q-icon
                    :name="passwordVisibility ? 'visibility_off' : 'visibility'"
                    class="cursor-pointer"
                    @click="passwordVisibility = !passwordVisibility"
                  />
                </template>
              </q-input>
            </div>

            <div class="password-criteria q-pa-sm">
              <div class="q-mb-sm text-weight-bold">Password Criteria:</div>
              <div>
                <q-icon
                  :name="validPassword.length ? 'check_circle' : 'cancel'"
                  :color="validPassword.length ? 'positive' : 'negative'"
                />
                Must contain at least 12 characters.
              </div>
              <div>
                <q-icon
                  :name="validPassword.lowercase ? 'check_circle' : 'cancel'"
                  :color="validPassword.lowercase ? 'positive' : 'negative'"
                />
                Must contain at least one lowercase character.
              </div>
              <div>
                <q-icon
                  :name="validPassword.uppercase ? 'check_circle' : 'cancel'"
                  :color="validPassword.uppercase ? 'positive' : 'negative'"
                />
                Must contain at least one uppercase character.
              </div>
              <div>
                <q-icon
                  :name="validPassword.number ? 'check_circle' : 'cancel'"
                  :color="validPassword.number ? 'positive' : 'negative'"
                />
                Must contain at least one numeric character.
              </div>
              <div>
                <q-icon
                  :name="validPassword.symbol ? 'check_circle' : 'cancel'"
                  :color="validPassword.symbol ? 'positive' : 'negative'"
                />
                Must contain at least one non-alphanumeric character: ^ $ * . [ ] { } ( ) ? " ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ `
              </div>
            </div>

            <div style="display: flex; alignItems: baseline">
              <div>Confirm Password *</div>
              <q-space />
              <q-input
                v-model="confirmPassword"
                filled
                style="width: 75%;"
                maxlength="256"
                :type="confirmPasswordVisibility ? 'password' : 'text'"
                :rules="[
                  (val) =>
                    (val && val === password) || 'Must match password.'
                ]"
                :disable="disabledFields"
              >
                <template v-slot:append>
                  <q-icon
                    :name="confirmPasswordVisibility ? 'visibility_off' : 'visibility'"
                    class="cursor-pointer"
                    @click="confirmPasswordVisibility = !confirmPasswordVisibility"
                  />
                </template>
              </q-input>
            </div>

            <br />

            <div style="display: flex; alignItems: baseline">
              <q-space />
              <q-btn
                label="Submit"
                type="submit"
                color="primary"
                no-caps
                :disable="disabledFields"
              />
              <q-btn
                label="Cancel"
                color="cancelButton"
                class="q-ml-sm"
                no-caps
                v-close-popup
                :disable="disabledFields"
              />
              <q-space />
            </div>
          </q-form>
        </q-card>
      </div>
    </div>
  </q-dialog>
</template>

<script>
import { ref } from "vue";
import NotificationHelper from "../helper/NotificationHelper";
import EmailService from "../service/EmailService.js";
import { auth } from "../firebase/config.js";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";

export default {
  name: "ChangePassword",
  setup() {
    const verifyCurrentPasswordDialog = ref(false);
    const changePasswordDialog = ref(false);
    const disabledFields = ref(false);

    const password = ref("");
    const passwordVisibility = ref(true);
    const confirmPassword = ref("");
    const confirmPasswordVisibility = ref(true);

    function displayChangePassword() {
      resetFields();
      verifyCurrentPasswordDialog.value = true;
    }

    async function submitCurrentPassword() {
      try {
        NotificationHelper.createNotification(
          "Verifying Password",
          {
            timeout: 0,
            type: "ongoing",
            color: "primary",
          }
        );
        disabledFields.value = true;

        const credential = EmailAuthProvider.credential(auth.currentUser.email, password.value);
        await reauthenticateWithCredential(auth.currentUser, credential);

        verifyCurrentPasswordDialog.value = false;
        changePasswordDialog.value = true;
        resetFields();
        disabledFields.value = false;
        NotificationHelper.dismiss();
      } catch (error) {
        NotificationHelper.dismiss();
        disabledFields.value = false;
        if (error.code === "auth/invalid-credential") {
          NotificationHelper.createNotification(
            "Invalid credential.",
            {
              timeout: 10000,
              type: "negative"
            }
          );
        } else if (error.code === "auth/too-many-requests") {
          NotificationHelper.createNotification(
            "Temporarily cannot change password due to many failed verification attempts. You can immediately restore it by signing out and resetting your password, or you can try again later.",
            {
              timeout: 10000,
              type: "negative"
            }
          );
        } else {
          EmailService.sendErrorEmail("ChangePassword.vue - submitCurrentPassword", error);
          NotificationHelper.createErrorNotification("Error encountered. Please try again.");
        }
      }
    }

    const validPassword = ref({
      length: false,
      lowercase: false,
      uppercase: false,
      number: false,
      symbol: false
    });

    function validatePassword() {
      // Test length: At least 12 characters long
      validPassword.value.length = password.value.length >= 12;

      // Test lowercase: At least one lowercase letter
      validPassword.value.lowercase = /^(?=.*[a-z])/.test(password.value);

      // Test uppercase: At least one uppercase letter
      validPassword.value.uppercase = /^(?=.*[A-Z])/.test(password.value);

      // Test number: At least one number
      validPassword.value.number = /^(?=.*[0-9])/.test(password.value);

      // Test symbol: At least one special character
      validPassword.value.symbol = /^(?=.*[\^\$*\.\[\]\{\}\(\)\?\"!@#%&\/\\,><':;|_~`])/.test(password.value);

      return (
        validPassword.value.length &&
        validPassword.value.lowercase &&
        validPassword.value.uppercase &&
        validPassword.value.number &&
        validPassword.value.symbol
      );
    }

    async function submitChangePassword() {
      try {
        NotificationHelper.createNotification(
          "Changing Password",
          {
            timeout: 0,
            type: "ongoing",
            color: "primary",
          }
        );
        disabledFields.value = true;

        await updatePassword(auth.currentUser, password.value);

        changePasswordDialog.value = false;
        NotificationHelper.dismiss();
        NotificationHelper.createSuccessNotification("Password Changed Successfully");
        disabledFields.value = false;
      } catch (error) {
        NotificationHelper.dismiss();
        disabledFields.value = false;
        if (error.code === "auth/password-does-not-meet-requirements") {
          NotificationHelper.createErrorNotification("Password does not meet requirements.");
        } else {
          EmailService.sendErrorEmail("ChangePassword.vue - submitChangePassword", error);
          NotificationHelper.createErrorNotification("Error encountered. Please try again.");
        }
      }
    }

    function resetFields() {
      password.value = "";
      passwordVisibility.value = true;
      confirmPassword.value = "";
      confirmPasswordVisibility.value = true;
      Object.keys(validPassword.value).forEach(key => validPassword.value[key] = false);
    }

    return {
      //variable properties
      verifyCurrentPasswordDialog,
      changePasswordDialog,
      disabledFields,
      password,
      confirmPassword,
      passwordVisibility,
      confirmPasswordVisibility,
      validPassword,

      //functions
      displayChangePassword,
      submitCurrentPassword,
      validatePassword,
      submitChangePassword
    };
  },
};

</script>

<style type="text/scss" lang="scss" scoped>
.password-criteria {
  background-color: #efefef;
  border-radius: 0.5rem;
}
</style>